import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";
import useAuth from "../useAuth";
import {
  ChevronLeft,
  ChevronRight,
  User,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  Calendar,
  Wifi,
  Search,
} from "lucide-react";
import Skeleton from "../Components/SkeletonLoader";

export default function RegisteredWifiUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const token = localStorage.getItem("dbToken");
  const { user } = useAuth();

  const fetchWifiUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${server}/wifiusers/getAll`, {
        params: {
          pageNo,
          pageSize,
          userWise: user?.userType === "admin" ? "false" : "true",
          ...(debouncedSearchTerm && { searchParams: debouncedSearchTerm }),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setUsers(response.data.data);
        setTotalCount(response.data.count);
      } else {
        toast.error("Failed to fetch WiFi users");
      }
    } catch (error) {
      console.error("Error fetching WiFi users:", error);
      toast.error(error.response?.data?.message || "Error fetching WiFi users");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      if (pageNo !== 1) setPageNo(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchWifiUsers();
  }, [token, pageNo, pageSize, debouncedSearchTerm]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= Math.ceil(totalCount / pageSize)) {
      setPageNo(newPage);
    }
  };

  const handleUpdateStatus = async (userId, newStatus) => {
    try {
      let response;

      if (newStatus !== "approved") return;

      if (newStatus === "approved") {
        response = await axios.patch(
          `${server}/wifiusers/approveUser?wifiUserId=${userId}`,
          {}, // Empty body
          {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "*/*",
            },
          },
        );
      }

      if (response.status === 200) {
        toast.success(`User status updated to ${newStatus}`);
        fetchWifiUsers(); // Refresh the list
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading && users.length === 0) {
    return (
      <div className="p-8 flex items-center justify-center text-gray-500">
        <div className="space-y-4 w-full max-w-6xl">
          <Skeleton className="h-8 w-48 mb-6" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-20 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <Wifi className="h-6 w-6 text-blue-600" />
            Registered WiFi Users
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage and view all registered WiFi users in the system.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder:text-gray-400"
            />
          </div>

          <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg shrink-0">
            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">
              Total Users: {totalCount}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <th className="px-6 py-4">User Profile</th>
                <th className="px-6 py-4">Contact Details</th>
                {/* <th className="px-6 py-4">Department</th>
                <th className="px-6 py-4">Policy</th> */}
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Registered Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-16 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="p-3 bg-gray-50 rounded-full">
                        <User className="h-6 w-6 text-gray-300" />
                      </div>
                      <span className="text-sm font-medium">
                        No WiFi users found
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className="group hover:bg-blue-50/30 transition-colors"
                  >
                    {/* User Profile */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-sm">
                          {user.fullName
                            ? user.fullName.charAt(0).toUpperCase()
                            : "U"}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900 text-sm">
                            {user.fullName || "Unknown User"}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <User className="h-3 w-3 text-gray-400" />
                            <span className="text-xs font-medium text-gray-500">
                              {user.username}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Contact Details */}
                    <td className="px-6 py-4">
                      <div className="space-y-1.5">
                        {user.phoneNumber && (
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <Phone className="h-3 w-3 text-gray-400" />
                            <span className="font-medium">
                              {user.phoneNumber}
                            </span>
                          </div>
                        )}
                        {user.email && (
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <Mail className="h-3 w-3 text-gray-400" />
                            <span className="font-medium truncate max-w-[200px]">
                              {user.email}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Department */}
                    {/* <td className="px-6 py-4">
                      {user.department ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-blue-500" />
                          <span className="text-sm font-medium text-gray-700">
                            {user.department}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td> */}

                    {/* Policy */}
                    {/* <td className="px-6 py-4">
                      {user.policiesMapperId ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                          {user.policiesMapperId}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td> */}

                    {/* Status */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2 items-start">
                        {/* Approval Status */}
                        {user.status && (
                          <div
                            className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${user.status === "approved"
                              ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                              : user.status === "rejected"
                                ? "text-red-700 bg-red-50 border-red-200"
                                : "text-amber-700 bg-amber-50 border-amber-200"
                              }`}
                          >
                            {user.status === "approved" ? (
                              <CheckCircle className="h-3 w-3" />
                            ) : user.status === "rejected" ? (
                              <XCircle className="h-3 w-3" />
                            ) : (
                              <div className="h-2 w-2 rounded-full bg-amber-500" />
                            )}
                            <span className="capitalize">{user.status}</span>
                          </div>
                        )}

                        {/* Verified Status */}
                        {user.isVerified ? (
                          <div className="flex items-center gap-1.5 text-xs font-bold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-200">
                            <CheckCircle className="h-3 w-3 fill-blue-500 text-white" />
                            <span>Verified</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-gray-50 px-2.5 py-1 rounded-full border border-gray-200">
                            <XCircle className="h-3 w-3" />
                            <span>Unverified</span>
                          </div>
                        )}

                        {/* Deleted Status */}
                        {user.deleted && (
                          <div className="flex items-center gap-1.5 text-xs font-bold text-red-700 bg-red-50 px-2.5 py-1 rounded-full border border-red-200">
                            <XCircle className="h-3 w-3" />
                            <span>Deleted</span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Registered Date */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="font-medium">
                            {user.createdAt
                              ? new Date(user.createdAt).toLocaleDateString()
                              : "—"}
                          </div>
                          <div className="text-xs text-gray-400">
                            {user.createdAt
                              ? new Date(user.createdAt).toLocaleTimeString(
                                [],
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )
                              : ""}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalCount > 0 && (
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-xs font-medium text-gray-500">
              Showing{" "}
              <span className="text-gray-900 font-bold">{users.length}</span> of{" "}
              <span className="text-gray-900 font-bold">{totalCount}</span> WiFi
              users
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pageNo - 1)}
                disabled={pageNo === 1}
                className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="px-4 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-mono font-medium text-gray-700 shadow-sm">
                Page {pageNo} of {totalPages || 1}
              </div>
              <button
                onClick={() => handlePageChange(pageNo + 1)}
                disabled={pageNo === totalPages || totalPages === 0}
                className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
